import type { LucideIcon } from 'lucide-react'
import {
  Building2,
  Calendar,
  CalendarClock,
  ChevronDown,
  ClipboardList,
  Package,
  PawPrint,
  Pill,
  PillBottle,
  Settings,
  ShoppingCart,
  UsersRound,
  Video,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

import logo from '@/frontend/assets/snoots-logo.png'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/frontend/components/ui/collapsible'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '@/frontend/components/ui/sidebar'
import { useCheckoutQueue } from '@/frontend/features/orders/hooks/use-checkout-queue'
import { useRemoteQueue } from '@/frontend/features/orders/hooks/use-remote-dispensing-queue'
import { useListMyTasks } from '@/frontend/features/tasks/use-list-my-tasks'
import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'
import { useUserSettings } from '@/frontend/hooks/use-user-settings'
import { routes } from '@/frontend/routes'

type NavItem = {
  label: string
  to: string
  icon: LucideIcon
}

type NavItemGroup = {
  label: string
  icon: LucideIcon
  navItems: NavItem[]
}

const baseNavItems: (NavItem | NavItemGroup)[] = [
  { label: 'Patients', to: routes.patients, icon: PawPrint },
  { label: 'Members', to: routes.members, icon: UsersRound },
  { label: 'Tasks', to: routes.tasks, icon: ClipboardList },
  { label: 'Appointments', to: routes.appointments, icon: Calendar },
  { label: 'Schedules', to: routes.schedule, icon: CalendarClock },
  { label: 'Remote Consultations', to: routes.remoteConsultations, icon: Video },
  { label: 'Checkout', to: routes.checkout, icon: ShoppingCart },
  { label: 'Remote Dispensing', to: routes.dispensing, icon: Pill },
  { label: 'Inventory', to: routes.inventory, icon: Package },
]

const adminNavGroup: NavItemGroup = {
  label: 'Admin',
  icon: Settings,
  navItems: [
    { label: 'Staff', to: '/admin/staff', icon: UsersRound },
    { label: 'Clinics', to: '/admin/clinics', icon: Building2 },
    { label: 'Product Catalog', to: '/admin/catalog-products', icon: PillBottle },
  ],
}

export function AppSidebar() {
  const { pathname } = useLocation()
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'
  const { data: authenticatedStaff } = useGetAuthenticatedStaff()
  const isSuperuser = authenticatedStaff?.isSuperuser
  const navItems = isSuperuser ? [...baseNavItems, adminNavGroup] : baseNavItems

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="pl-5 h-18 flex justify-center">
        {!isCollapsed && (
          <Link to="/">
            <img
              src={logo}
              alt="Snootsvet"
              className="h-6 w-auto self-start object-contain group-data-[collapsible=icon]:hidden"
            />
          </Link>
        )}
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => {
                if ('navItems' in item) {
                  return (
                    <CollapsibleGroup key={item.label} isCollapsed={isCollapsed}>
                      <SidebarMenuItem>
                        <SidebarMenuButton render={<CollapsibleTrigger />} tooltip={item.label}>
                          <item.icon />
                          {item.label}
                          <ChevronDown className="ml-auto size-4 transition-transform group-data-[state=closed]/collapsible:rotate-[-90deg]" />
                        </SidebarMenuButton>
                        <CollapsibleContent>
                          <SidebarMenu className="pl-4">
                            {item.navItems.map((child) => (
                              <SidebarMenuItem key={child.to}>
                                <SidebarMenuButton
                                  render={<NavLink to={child.to} />}
                                  isActive={pathname === child.to}
                                  tooltip={child.label}
                                >
                                  <child.icon />
                                  <span>{child.label}</span>
                                </SidebarMenuButton>
                              </SidebarMenuItem>
                            ))}
                          </SidebarMenu>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </CollapsibleGroup>
                  )
                }

                return (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton
                      render={<NavLink to={item.to} />}
                      isActive={item.to === '/' ? pathname === '/' : pathname.startsWith(item.to)}
                      tooltip={item.label}
                    >
                      <item.icon />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                    {item.to === routes.tasks && <TaskCountBadge />}
                    {item.to === routes.checkout && <CheckoutCountBadge />}
                    {item.to === routes.dispensing && <DispensingCountBadge />}
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <div className="flex justify-end">
          <SidebarTrigger />
        </div>
      </SidebarFooter>
    </Sidebar>
  )
}

/** Shared class for pending count badges */
const COUNT_BADGE_CLASS = 'bg-pending-accent rounded-full'

/**
 * Displays the user's outstanding task count as a sidebar badge: personal
 * assignments plus team assignments (scoped to the user's default clinic for
 * clinic-bound roles).
 */
function TaskCountBadge() {
  // No clinicId passed — the hook falls back to the user's default clinic for
  // clinic-bound roles. `limit: 1` since we only need the (server-computed)
  // totalCount here, not the rows.
  const { data } = useListMyTasks(
    {
      status: ['open', 'in_progress'],
      limit: 1,
    },
    // Poll so the badge stays roughly current even when tasks are completed by
    // a colleague (which wouldn't otherwise invalidate this client's cache).
    { refreshInterval: 60_000 }
  )

  const count = data?.totalCount ?? 0

  if (!count) return null

  return <SidebarMenuBadge className={COUNT_BADGE_CLASS}>{count}</SidebarMenuBadge>
}

/** Displays the checkout queue patient count for the user's default clinic. */
function CheckoutCountBadge() {
  const { settings } = useUserSettings()
  const clinicId = settings?.defaultClinic?.id

  const { totalPatientCount } = useCheckoutQueue({ clinicId })

  if (!totalPatientCount) return null

  return <SidebarMenuBadge className={COUNT_BADGE_CLASS}>{totalPatientCount}</SidebarMenuBadge>
}

/** Displays the pending remote dispensing patient count for the user's default clinic. */
function DispensingCountBadge() {
  const { settings } = useUserSettings()
  const clinicId = settings?.defaultClinic?.id

  const { totalPatientCount } = useRemoteQueue({
    clinicId,
    view: 'pending',
  })

  if (!totalPatientCount) return null

  return <SidebarMenuBadge className={COUNT_BADGE_CLASS}>{totalPatientCount}</SidebarMenuBadge>
}

/**
 * Wrapper around Collapsible that stays fully controlled.
 * Defaults to open, but forces closed when the sidebar is in icon-only mode.
 */
function CollapsibleGroup({
  isCollapsed,
  children,
  ...rest
}: { isCollapsed: boolean; children: React.ReactNode } & Record<string, unknown>) {
  const [userOpen, setUserOpen] = useState(true)
  const open = isCollapsed ? false : userOpen

  // Re-open groups when sidebar expands (so they don't stay collapsed after toggling)
  useEffect(() => {
    if (!isCollapsed) setUserOpen(true)
  }, [isCollapsed])

  return (
    <Collapsible
      open={open}
      onOpenChange={setUserOpen}
      className="group/collapsible"
      data-state={open ? 'open' : 'closed'}
      {...rest}
    >
      {children}
    </Collapsible>
  )
}
